import React, { useState, useMemo, useRef, useEffect } from 'react';
import { getLocale } from 'umi';
import ChartsIndex from '@/pages/intelligence/components/ChartsComponents/ChartsIndex';
import useBarCharts from './useBarChart';
import useSinglePieCharts from '@/pages/intelligence/components/Chart/hooks/useSinglePieCharts';
import useLineCharts from '@/pages/intelligence/components/Chart/hooks/useLineCharts';
import { cardMetricMap } from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Demographics/_config/constant';
import { showTimeByDateType } from '@/pages/intelligence/utils/commonUtils';
import moment from 'moment';
import { ageAddPreText } from '../../_config/utils';
import { PAGE_TYPE_AGE, PAGE_TYPE_GENDER } from '../../_config/constant';
import { maleColors } from '@intelligence/pages/TopCharts/MobileGames/Demographics/_config/utils';
import type { IChartsIndexInstance } from '@intelligence/components/ChartsComponents/ChartsIndex';

const PIE_TITLE_TEXT_STYLE = {
  fontSize: 12,
  lineHeight: 18,
} as const;

// 饼图标签格式化：显示为 "Name: 50.30%" 格式
const pieLabelFormatter = (params: any) => {
  const metricName = params?.name || '';
  const percent = params?.percent;
  const percentStr = typeof percent === 'number' ? percent.toFixed(2) : '-';
  return `{context|${metricName}: ${percentStr}%}\n{hr|}\n{a|}`;
};

const Visualis: React.FC<any> = (props: any) => {
  const {
    pieData,
    middleTitle,
    chartType,
    imageType = 'mobile',
    lineData,
    dateType,
    startDate,
    endDate,
    pageType,
    barData,
    lengendKeys,
    rangeTimeType,
  } = props;

  const locale = getLocale();
  const pieTitleTop = locale === 'zh-CN' ? '45%' : '44%';

  const cardRef = useRef(null);
  const chartsInstance = useRef<IChartsIndexInstance>(null);
  const [chartLoading, setChartLoading] = useState(false);
  // 横坐标的数据
  const [barAxisLabelData, setBarAxisLabelData] = useState<string[]>([]);
  // 实例的数据
  const [barSeriesData, setBarSeriesData] = useState([]);

  const isQuarter = rangeTimeType === 'quarter';
  const dateTypeForBubble = isQuarter ? '' : dateType;

  const pieTimeText = useMemo(() => {
    if (!isQuarter) return '';
    const base = startDate || endDate;
    const m = moment(base);
    return m.isValid() ? m.format('YYYY-[Q]Q') : '';
  }, [isQuarter, startDate, endDate]);

  const pieSeriesData = useMemo(() => {
    const piList: any[] = [];
    lengendKeys?.forEach((key: any) => {
      pieData?.forEach((item: any) => {
        if (item?.metric === key) {
          const metricNum = parseFloat(item?.value).toFixed(1); // TODO这里外面转换了，要保持饼图数据和头部卡片小数一样
          const nameStr = cardMetricMap[item?.metric];
          piList.push({
            app_id: item?.metric,
            name: pageType === PAGE_TYPE_AGE ? ageAddPreText(nameStr) : nameStr,
            value: metricNum,
          });
        }
      });
    });
    return piList;
  }, [pieData, lengendKeys, pageType]);

  const formatQuarter = (value: string) => {
    const m = moment(value);
    return m.isValid() ? m.format('YYYY-[Q]Q') : '';
  };

  const lineXAxisData = useMemo(() => {
    if (!Array.isArray(lineData)) {
      return [];
    }
    const sorted = [...lineData].sort((a, b) => moment(a?.date).valueOf() - moment(b?.date).valueOf());
    const labels: string[] = [];
    sorted.forEach((item: any) => {
      const dateStr = item?.date;
      const label = isQuarter ? formatQuarter(dateStr) : showTimeByDateType(dateStr, dateType);
      if (label && !labels.includes(label)) {
        labels.push(label);
      }
    });
    return labels;
  }, [lineData, isQuarter, dateType]);

  const lineSeriesData = useMemo(() => {
    const newData: any[] = [];
    if (!Array.isArray(lineData)) {
      return { metrics: newData };
    }

    const sorted = [...lineData].sort((a, b) => moment(a?.date).valueOf() - moment(b?.date).valueOf());

    sorted.forEach((item: any) => {
      const dateStr = item?.date;
      const timeLabel = isQuarter ? formatQuarter(dateStr) : showTimeByDateType(dateStr, dateType);

      (item?.metrics || []).forEach((metric: any) => {
        const existingMetric = newData.find((coverage) => coverage?.id === metric?.metric);
        const point = {
          time: timeLabel,
          value: (metric?.value * 100).toFixed(1),
        };

        if (existingMetric) {
          existingMetric.history.push(point);
        } else {
          const nameStr = cardMetricMap[metric?.metric];
          newData.push({
            cover: metric?.metric,
            id: metric?.metric,
            name: nameStr,
            history: [point],
          });
        }
      });
    });

    return {
      metrics: newData,
    };
  }, [lineData, isQuarter, dateType]);

  useEffect(() => {
    if (!Array.isArray(barData)) {
      return;
    }
    // 按日期升序排序 barData
    const sortedBarData = [...barData].sort(
      (a, b) => moment(a?.date).valueOf() - moment(b?.date).valueOf(),
    );
    // 从排序后的数据中提取 xAxis 标签（日期）
    const xAxisData = sortedBarData?.map((item) =>
      isQuarter ? formatQuarter(item?.date) : item?.date,
    );
    // 为每个图例项生成 seriesData

    const seriesData = lengendKeys?.map((item: any) => {
      const data: number[] = [];
      // 遍历 sortedBarData，查找当前图例项的值
      sortedBarData.forEach((chart: any) => {
        const metricItem = chart?.metrics.find((t: any) => t?.metric === item);
        data.push(metricItem?.value);
      });

      // 返回当前图例项的系列配置
      return {
        name: cardMetricMap[item],
        type: 'bar',
        stack: 'count',
        barMaxWidth: 80,
        itemStyle: {
          // color: colors[index]
        },
        data,
      };
    });

    // 更新 xAxis 标签和系列数据
    setBarAxisLabelData(xAxisData);
    setBarSeriesData(seriesData);
  }, [barData, lengendKeys, isQuarter]);

  const pieLegendsCustomMade = useMemo(() => {	
    // Gender 的饼图右侧使用自定义图例（`GenderRatioChart`），因此隐藏 echarts 自带 legend。	
    // Age Breakdown(%) 需要展示图例。	
    if (pageType === PAGE_TYPE_GENDER && chartType === 'pie') {	
      return { show: false };	
    }	
    return undefined;	
  }, [pageType, chartType]);

  const { pieOption } = useSinglePieCharts({
    legendsCustomMade: pieLegendsCustomMade,
    pieData: pieSeriesData,
    pieTitle: middleTitle,
    setChartLoading,
    chartLoading,
    chartType,
    colors: pageType === PAGE_TYPE_GENDER ? maleColors : undefined,
    chartsInstance,
    titleNow: 'Demographic Percentage',
    startDate,
    endDate,
    timeText: pieTimeText,
    labelFortmatter: pieLabelFormatter,
    pieTitleTextStyle: PIE_TITLE_TEXT_STYLE,
    pieTitleTop,
  });

  // 获取折线图hooks
  const { lineOption } = useLineCharts({
    lineData: lineSeriesData,
    metric: 'metrics',
    lineXAxisData,
    xAxisAutoSort: false,
    imageType,
    chartType,
    dateType: dateTypeForBubble,
    isPercentLine: true,
    colors: pageType === PAGE_TYPE_GENDER ? maleColors : undefined,
    titleNow: 'Demographic Percentage',
    topTitle: pageType === PAGE_TYPE_GENDER ? 'Gender' : 'Age',
    topChartsType: 'detail-demographics',
  });

  const { barOption } = useBarCharts({
    barData: barData,
    axisLabelData: barAxisLabelData,
    seriesData: barSeriesData,
    colors: pageType === PAGE_TYPE_GENDER ? maleColors : undefined,
    dateType: dateTypeForBubble,
    titleNow: 'Demographic Percentage',
    topTitle: pageType === PAGE_TYPE_GENDER ? 'Gender' : 'Age',
  });

  return (
    <>
      <div className="card-line" ref={cardRef} style={{ width: '100%' }}>
        <ChartsIndex
          ref={chartsInstance}
          chartType={chartType}
          chartLoading={chartLoading}
          lineOption={lineOption}
          // isNodata={isNodata}
          pieOption={pieOption}
          barOption={barOption}
        />
      </div>
    </>
  );
};

export default Visualis;
